import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  EditOutlined as Edit,
  Cancel as Remove,
  ListOutlined,
} from '@mui/icons-material';
import { PlusIcon, XMarkIcon, PencilIcon } from '@heroicons/react/24/outline';

// utilities
import { useSnackbar } from '../../utils/useSnackbar';
import { setActivePage } from '../../features/navigation/navigationSlice';
import { formatEnum } from '../../utils/string';
import { UserStatusTypes } from "../../enums/UserEnums";
import { useGridApiRef } from '@mui/x-data-grid';
import { getProjectUsers, removeProjectUsers } from '../../utils/api/user_apis';
import { STATUS } from '../../utils/api/sharedfunction';

// components
import PageHeader from '../Header/PageHeader';
import ConfirmationModal from '../Modal/ConfirmationModal';
import ActionMenu from '../menu/ActionMenu';
import AddUsersToProjectModal from '../Modal/AddUsersToProjectModal';
import EditProjectUserModal from '../Modal/EditProjectUserModal';
import StatusChip from '../chips/dataGridchips/StatusChip';
import TableComponent from '../TableComponent';
import DEFAULT_ROUTES from "../../constants/routes";
import RowActionMenu from '../menu/RowActionMenu';
import styles from '../../styles/component.module.scss';
import TagsComponent from '../Tags';
import ChipComponent from '../chips/ChipComponent/ChipComponent';
import { getUserStatusVariant } from '../chips/ChipComponent/chipVariants';
import { renderTableCell } from '../../utils/commonfunctions';
import ExpandableCell from '../datagrid/ExpandableCell/ExpandableCell';

const getStatusAndText = (status) => {
  let type;  
  switch (status) {
    case UserStatusTypes.ACTIVE:
      type = "True";  
      break;
    case UserStatusTypes.INACTIVE:
      type = "False";
      break;
    case UserStatusTypes.DISABLED:
      type = "Disabled";
      break;  
  }
  const text = formatEnum(status);
  return <StatusChip status={type} text={text} />;  
};

const ProjectUsers = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { showSnackbar } = useSnackbar();
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const activePage = useSelector((state) => state.navigation.activePage);
  const user = useSelector((state) => state.auth.user);
  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [userForAction, setUserForAction] = useState(null);
  const isMultiAction = selectedRowIds.length > 0;

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25
  });

  useEffect(() => {
    dispatch(setActivePage('Project Team'));
    fetchUsers();
  }, [dispatch, selectedProject]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getProjectUsers({ projectId: selectedProject.id });
      if (response.status === STATUS.SUCCESS) {
        setUsers(response.data || []);
      } else {
        showSnackbar(response.error || 'Failed to fetch project team', 'error');
      }
    } catch (error) {
      console.error('Failed to fetch project team:', error);
      showSnackbar('Failed to fetch project team', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmRemove = async () => {
    try {
      setLoading(true);
      const userIdsToRemove = isMultiAction ? selectedRowIds : [userForAction.id];
      const isRemovingSelf = userIdsToRemove.includes(user.id);

      const response = await removeProjectUsers({ 
        userIds: userIdsToRemove,
        projectId: selectedProject.id
      });

      if (response.status === STATUS.SUCCESS) {
        if (isRemovingSelf) {
          // If user removed themselves, show special message and redirect
          showSnackbar('You have been removed from this project successfully', 'success');
          // Redirect to projects page after a short delay
          setTimeout(() => {
            navigate(`${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}`);
          }, 2000);
        } else {
          showSnackbar(response.message || 'User(s) removed successfully', 'success');
          fetchUsers();
        }
      } else {
        showSnackbar(response.error || 'Failed to remove user(s)', 'error');
      }
    } catch (error) {
      console.error('Error removing user(s):', error);
      showSnackbar('Failed to remove user(s)', 'error');
    } finally {
      setIsRemoving(false);
      setLoading(false);
    }
  };

  // Row action handlers
  const handleUserEdit = (user) => {
    setUserForAction(user);
    setIsEditing(true);
  };

  const handleUserRemove = (user) => {
    setUserForAction(user);
    setIsRemoving(true);
  };

  // header action handlers
  const handleAdd = () => {
    setIsAdding(true);
  };

  const handleRemove = () => {
    setIsRemoving(true);
  };

  // Handler function after user operations
  const afterUserAdded = () => {
    fetchUsers();
    showSnackbar('User Added successfully', 'success');
  };

  const afterUserRemoved = () => {
    fetchUsers();
    showSnackbar('User Added successfully', 'success');
  };

  const afterUserUpdated = () => {
    fetchUsers();
    const message = userForAction?.id === user.id 
      ? 'Your roles have been updated successfully'
      : 'User updated successfully';
    showSnackbar(message, 'success');
  }

  function handleBreadcrumbClick(id) {
    let {rURL, activePage=null} = (() => {
      if (id == "home") {
        return {
          rURL: DEFAULT_ROUTES.BASE_URL,
          activePage: "Projects",
        };
      }
      if (id == "project") {
        return {
          rURL: `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject.id}/test-cases`,
          activePage: "Test Cases",
        };
      }
    })();
    if (activePage) {
      dispatch(setActivePage(activePage));
    }
    navigate(rURL);
  }

  const breadcrumbItems = [
    {
      label: "Project",
      onClick: () => handleBreadcrumbClick("home"),
    },
    {
      label: selectedProject?.name || "Unknown Project",
      onClick: () => handleBreadcrumbClick("project"),
    },
    { label: activePage },
  ];

  const rowActionButtons = [
    {
      name: 'Edit',
      tooltip: 'Edit User',
      icon: <PencilIcon className='icon' />,
      action: (user) => handleUserEdit(user),
      disabled: false,
    },
    {
      name: 'Remove',
      tooltip: 'Remove User',
      icon: <XMarkIcon className='icon' />,
      action: (user) => handleUserRemove(user),
      disabled: false,
    },
  ];

  const columns = [
    {
      field: '__check__',
      hideable: false,
      sortable: false,
      filterable: false,
      minWidth: 55,
      maxWidth: 55,
      resizable: false,
      headerAlign: 'center',
      align: 'center',        
    },
    { 
      field: 'username', 
      headerName: 'Username', 
      flex: 2,
      minWidth: 180,
      renderCell: (params) => 
        renderTableCell(
          null, null, 
          {
            children: (
              <>
                {params.value}
                {params.row.id === user.id && (
                  <Chip 
                    label="You" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ fontSize: '10px', ml:0.4, height: 'fit-content' }}
                  />
                )}
              </>
            )
          }
        )
    },
    { 
      field: 'email', 
      headerName: 'Email', 
      flex: 2,
      minWidth: 300,
      maxWidth: 350,
      renderCell: (params) => renderTableCell(params.value)
    },
    {
      field: 'roles',
      headerName: 'Role',
      flex: 2,
      minWidth: 500,
      renderCell: (params) => {
        const roles= params.value.map((role) => role.name).join(', ');
        return <ExpandableCell
          value={roles ?? MissingData.NONE}
          maxLength={100}
        />
      }
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.5,
      minWidth: 130,
      maxWidth: 230,
      renderCell: (params) => (
        <ChipComponent
          variant={getUserStatusVariant(params.value)}
          size="small"
        >
          {params.value}
        </ChipComponent>
      )
    },
    {
      field: 'Actions',
      headerName: 'Actions',
      headerAlign: 'center',
      flex: 1,
      sortable: false,
      filterable: false,
      align: 'center',
      disableColumnMenu: true,
      resizable: false,
      minWidth: 90,
      maxWidth: 90,
      hideable: false,
      renderCell: (params) => (
        <RowActionMenu 
          menuItems={rowActionButtons} 
          rowData={params.row} 
          isDisabled={false}
        />
      ),
    },
  ];

  const headerActionButtons = [
    {
      name: 'Add',
      tooltip: 'Add New User',
      icon: <PlusIcon className='icon' />,
      action: handleAdd,
      disabled: loading,
      filled: true,
    },
    {
      name: 'Remove',
      tooltip: 'Remove Selected User(s)',
      icon: <XMarkIcon className='icon' />,
      action: handleRemove,
      disabled: selectedRowIds.length === 0 || loading,
    },
  ];

  return (
    <Box className={styles['page-container']}>
      <PageHeader
        projectName={selectedProject?.name}
        title="Project Users"
        description="Manage users and roles for this project."
        activePage={activePage}
        breadcrumbItems={breadcrumbItems}
        actions={[]}
      />
      <Box className={styles['cover-container-col']} sx={{ width: '100%', paddingX: 2, paddingTop: 0 }}>
        <TableComponent
          title={`Project Team List: ${selectedProject.name}`}
          headerActionButtons={headerActionButtons}
          apiRef={apiRef}
          rows={users}
          columns={columns}
          loading={loading}
          idField="id"
          isCheckbox={true}
          isRowSelection={true}
          handleRowSelection={(selectionModel) => setSelectedRowIds(selectionModel)}
          selectedRows={selectedRowIds}
          paginationModel={paginationModel}
          handleCustomPaginationPageChange={(event, page) => setPaginationModel({...paginationModel, page: page})}
          handleCustomPaginationPageSizeChange={(pageSize) => setPaginationModel({page: 0, pageSize: pageSize})}
          isCustomPagination={true}
          isColumnResizable={!loading}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 100}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          hideColumns={['username']}
        />
      </Box>

      {/* Add User */}
      <AddUsersToProjectModal
        isOpen={isAdding}
        onClose={() => setIsAdding(false)}
        onAdd={afterUserAdded}
        onError={(message, severity) => showSnackbar(message, severity)}
      />

      {/* Edit User */}
      <EditProjectUserModal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        onUpdate={afterUserUpdated}
        onError={(message, severity) => showSnackbar(message, severity)}
        data={userForAction}
      />

      {/* Remove Confirmation */}
      <ConfirmationModal
        open={isRemoving}
        onClose={() => setIsRemoving(false)}
        onConfirm={handleConfirmRemove}
        title={
          isMultiAction
            ? `Are you sure you want to remove ${selectedRowIds.length} user(s)?`
            : userForAction?.id === user.id
              ? 'Remove Yourself from Project'
              : 'Are you sure you want to remove this user?'
        }
        message={
          isMultiAction
            ? selectedRowIds.includes(user.id)
              ? "You are trying to remove yourself from the project. This action cannot be undone. Please confirm if you want to proceed."
              : "You are about to remove user(s) from the project. This action cannot be undone. Please confirm if you want to proceed."
            : userForAction?.id === user.id
              ? "You are trying to remove yourself from the project. This action cannot be undone. Please confirm if you want to proceed."
              : "You are about to remove user(s) from the project. This action cannot be undone. Please confirm if you want to proceed."
        }
      />
    </Box>
  );
};

export default ProjectUsers;
